import React from 'react';
import { Phone, Instagram, Facebook, MapPin, Clock, Heart, Sparkles } from 'lucide-react';
import { salonInfo } from '../data/mock';

export const Footer = () => {
  const openWhatsApp = () => {
    const message = `¡Hola! Me gustaría obtener más información sobre ${salonInfo.name}.`;
    const whatsappUrl = `https://wa.me/${salonInfo.whatsapp.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative overflow-hidden bg-black">
      {/* Top decorative border - Gold gradient */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />

      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-4">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <img 
                src={salonInfo.logo} 
                alt="Saldívar Villar Logo" 
                className="h-16 w-16 rounded-full object-cover border-2 border-[#d4af37]/30"
              />
              <div>
                <h3 className="font-serif text-lg font-semibold text-white">
                  Saldívar Villar
                </h3>
                <p className="text-sm text-[#d4af37]">Salón & Spa</p>
              </div>
            </div>
            <p className="mt-4 text-neutral-400">
              Tu santuario de belleza y bienestar. Donde cada visita es una experiencia transformadora.
            </p>
            
            {/* Social Links */}
            <div className="mt-6 flex gap-3">
              <a
                href={salonInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-800 text-neutral-400 transition-all hover:bg-[#d4af37] hover:text-neutral-900 border border-neutral-700"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={salonInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-800 text-neutral-400 transition-all hover:bg-[#d4af37] hover:text-neutral-900 border border-neutral-700"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <button
                onClick={openWhatsApp}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-800 text-neutral-400 transition-all hover:bg-green-500 hover:text-white border border-neutral-700"
              >
                <Phone className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white">Navegación</h4>
            <div className="mt-2 h-px w-12 bg-gradient-to-r from-[#d4af37] to-transparent" />
            <ul className="mt-4 space-y-3">
              {[
                { label: 'Inicio', href: '#hero' },
                { label: 'Nosotros', href: '#about' },
                { label: 'Servicios', href: '#services' },
                { label: 'Reservar', href: '#booking' },
                { label: 'Sucursales', href: '#locations' },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-neutral-400 transition-colors hover:text-[#d4af37]"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-white">Contacto</h4>
            <div className="mt-2 h-px w-12 bg-gradient-to-r from-[#d4af37] to-transparent" />
            <ul className="mt-4 space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#d4af37]" />
                <div>
                  <p className="text-white">{salonInfo.whatsappFormatted}</p>
                  <p className="text-sm text-neutral-500">WhatsApp</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#d4af37]" />
                <div>
                  <p className="text-white">Toluca, Estado de México</p>
                  <p className="text-sm text-neutral-500">Sucursal principal</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-semibold text-white">Horarios</h4>
            <div className="mt-2 h-px w-12 bg-gradient-to-r from-[#d4af37] to-transparent" />
            <ul className="mt-4 space-y-3">
              <li className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-[#d4af37]" />
                <div>
                  <p className="text-white">Lunes - Viernes</p>
                  <p className="text-sm text-neutral-400">9:00 AM - 8:00 PM</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-[#d4af37]" />
                <div>
                  <p className="text-white">Sábado</p>
                  <p className="text-sm text-neutral-400">9:00 AM - 6:00 PM</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-neutral-600" />
                <div>
                  <p className="text-neutral-500">Domingo</p>
                  <p className="text-sm text-neutral-600">Cerrado</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-neutral-800 pt-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-neutral-500">
              © {new Date().getFullYear()} {salonInfo.name}. Todos los derechos reservados.
            </p>
            <p className="flex items-center gap-1 text-sm text-neutral-500">
              Hecho con <Heart className="h-4 w-4 text-[#d4af37]" fill="currentColor" /> para tu bienestar
            </p>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:bg-green-600 hover:shadow-xl"
        aria-label="Contactar por WhatsApp"
      >
        <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </button>

      {/* Decorative quote */}
      <div className="relative bg-neutral-900 py-8 border-t border-neutral-800">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <Sparkles className="mx-auto h-6 w-6 text-[#d4af37]" />
          <p className="mt-4 font-serif text-lg italic text-neutral-300">
            &ldquo;La belleza comienza en el momento en que decides ser tú misma&rdquo;
          </p>
          <p className="mt-2 text-sm text-neutral-500">- Coco Chanel</p>
        </div>
      </div>
    </footer>
  );
};
